import { Link } from 'react-router';
import { CheckCircle2, Circle, ChevronRight } from 'lucide-react';

export interface OnboardingStep {
  label: string;
  description: string;
  done: boolean;
  href: string;
  icon: React.ReactNode;
}

export function OnboardingChecklist({ steps }: { steps: OnboardingStep[] }) {
  const doneCount = steps.filter((s) => s.done).length;
  const currentIndex = steps.findIndex((s) => !s.done);

  return (
    <div className="glass-sm rounded-lg overflow-hidden">
      {/* Header */}
      <div className="p-4 pb-2">
        <div className="flex items-center justify-between mb-1">
          <h2 className="text-sm font-extrabold">Getting Started</h2>
          <span className="text-xs font-semibold text-ink-3">
            {doneCount} of {steps.length}
          </span>
        </div>
        <p className="text-xs text-ink-3 leading-relaxed">
          Follow these steps to deploy your first trading bot. Each step builds on the last — start by creating a strategy from your trade history or from scratch.
        </p>
      </div>

      {/* Steps */}
      <div className="px-2 pb-2">
        {steps.map((step, i) => {
          const isCurrent = i === currentIndex;

          return (
            <Link
              key={step.label}
              to={step.href}
              className={`flex items-center gap-3 rounded-md px-3 py-2.5 transition-colors ${
                isCurrent
                  ? 'bg-orange/5 border-l-2 border-orange'
                  : 'border-l-2 border-transparent'
              } ${step.done ? '' : 'hover:bg-surface-2'}`}
            >
              {/* Icon */}
              <span className="flex-shrink-0">
                {step.done ? (
                  <CheckCircle2 size={18} className="text-green" />
                ) : (
                  <Circle
                    size={18}
                    className={isCurrent ? 'text-orange' : 'text-ink-4'}
                  />
                )}
              </span>

              {/* Label + description */}
              <div className="flex-1 min-w-0">
                <p
                  className={`text-sm font-semibold ${
                    step.done
                      ? 'text-ink-3 line-through'
                      : isCurrent
                        ? 'text-ink-1'
                        : 'text-ink-4'
                  }`}
                >
                  {step.label}
                </p>
                {!step.done && (
                  <p className="text-xs text-ink-3 mt-0.5">
                    {step.description}
                  </p>
                )}
              </div>

              {/* CTA chevron */}
              {isCurrent && (
                <ChevronRight size={16} className="text-orange flex-shrink-0" />
              )}
            </Link>
          );
        })}
      </div>
    </div>
  );
}
